import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Avatar,
  Skeleton,
  Fab,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  LocalShipping as TruckIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Badge as BadgeIcon,
  Notes as NotesIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { veicoliService } from '../services/api';

// Componente StatBox riutilizzabile
const StatBox = ({ icon, value, label }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      mb: 0.5
    }}>
      {icon && React.cloneElement(icon, { sx: { color: 'rgba(255,255,255,0.7)', fontSize: '1.2rem' } })}
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
      {label}
    </Typography>
  </Box>
);

const Veicoli = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.auth);
  const [veicoli, setVeicoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentVeicolo, setCurrentVeicolo] = useState(null);
  const [veicoloToDelete, setVeicoloToDelete] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    targa: '',
    modello: '',
    tipo: '',
    note: '',
    attivo: true,
  });

  useEffect(() => {
    loadVeicoli();
  }, []);

  const loadVeicoli = async () => {
    try {
      setLoading(true);
      const response = await veicoliService.getAll();
      setVeicoli(response.data);
    } catch (error) {
      setError('Errore nel caricamento dei veicoli');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (veicolo = null) => {
    if (veicolo) {
      setCurrentVeicolo(veicolo);
      setFormData({
        targa: veicolo.targa,
        modello: veicolo.modello,
        tipo: veicolo.tipo || '',
        note: veicolo.note || '',
        attivo: veicolo.attivo,
      });
    } else {
      setCurrentVeicolo(null);
      setFormData({
        targa: '',
        modello: '',
        tipo: '',
        note: '',
        attivo: true,
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentVeicolo(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.targa || !formData.modello) {
        setError('Targa e modello sono obbligatori');
        return;
      }

      if (currentVeicolo) {
        await veicoliService.update(currentVeicolo.id, formData);
      } else {
        await veicoliService.create(formData);
      }
      loadVeicoli();
      handleCloseDialog();
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const handleOpenDeleteDialog = (veicolo) => {
    setVeicoloToDelete(veicolo);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setVeicoloToDelete(null);
  };

  const handleDelete = async () => {
    try {
      await veicoliService.delete(veicoloToDelete.id);
      loadVeicoli();
      handleCloseDeleteDialog();
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nell\'eliminazione del veicolo');
      handleCloseDeleteDialog();
    }
  };

  // Filtra veicoli
  const filteredVeicoli = veicoli.filter((veicolo) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      veicolo.targa?.toLowerCase().includes(search) ||
      veicolo.modello?.toLowerCase().includes(search) ||
      veicolo.tipo?.toLowerCase().includes(search)
    );
  });

  // Reset pagina quando cambia il filtro
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedVeicoli = filteredVeicoli.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Statistiche
  const stats = {
    totali: veicoli.length,
    attivi: veicoli.filter(v => v.attivo).length,
    inattivi: veicoli.filter(v => !v.attivo).length,
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'amministratore';

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="rounded" height={200} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 4 } }}>
      {/* Header moderno con sfondo scuro */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          color: 'white',
          pt: { xs: 2, sm: 4 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: 'radial-gradient(circle, rgba(229,0,0,0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Titolo e bottone */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  bgcolor: '#e50000',
                  boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                }}
              >
                <CarIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    letterSpacing: '-0.5px',
                  }}
                >
                  {isMobile ? 'Veicoli' : 'Gestione Veicoli'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                  {filteredVeicoli.length} veicoli {searchTerm ? 'trovati' : 'totali'}
                </Typography>
              </Box>
            </Box>

            {isAdmin && !isMobile && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  bgcolor: '#e50000',
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                  '&:hover': {
                    bgcolor: '#c50000',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(229,0,0,0.5)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Nuovo Veicolo
              </Button>
            )}
          </Box>

          {/* Statistiche */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: { xs: 2, sm: 3 },
              mt: 3,
            }}
          >
            <StatBox
              icon={<CarIcon />}
              value={stats.totali}
              label="Totali"
            />
            <StatBox
              icon={<CheckCircleIcon />}
              value={stats.attivi}
              label="Attivi"
            />
            <StatBox
              icon={<CancelIcon />}
              value={stats.inattivi}
              label="Inattivi"
            />
          </Box>
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Alert errore */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {/* Barra di ricerca */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: searchFocused ? '0 4px 20px rgba(229,0,0,0.1)' : '0 2px 12px rgba(0,0,0,0.04)',
            transition: 'all 0.3s ease',
          }}
        >
          <TextField
            fullWidth
            placeholder="Cerca per targa, modello o tipo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: searchFocused ? '#e50000' : 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'grey.50',
                '&:hover': { bgcolor: 'grey.100' },
                '&.Mui-focused': {
                  bgcolor: 'white',
                  '& fieldset': { borderColor: '#e50000', borderWidth: 2 },
                },
              },
            }}
          />
        </Paper>

        {/* Lista veicoli */}
        {filteredVeicoli.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CarIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'Nessun veicolo trovato' : 'Nessun veicolo disponibile'}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {searchTerm ? 'Prova a modificare i criteri di ricerca' : 'Aggiungi il primo veicolo per iniziare'}
            </Typography>
          </Paper>
        ) : isMobile ? (
          /* Vista Mobile - Cards */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paginatedVeicoli.map((veicolo) => (
              <Card
                key={veicolo.id}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#e50000',
                    boxShadow: '0 8px 24px rgba(229,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  {/* Header card */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: veicolo.attivo ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                          color: veicolo.attivo ? '#4caf50' : '#9e9e9e',
                        }}
                      >
                        <CarIcon />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: '#e50000',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {veicolo.targa}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {veicolo.modello}
                        </Typography>
                      </Box>
                    </Box>

                    <Chip
                      icon={veicolo.attivo ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : <CancelIcon sx={{ fontSize: '14px !important' }} />}
                      label={veicolo.attivo ? 'Attivo' : 'Inattivo'}
                      size="small"
                      sx={{
                        bgcolor: veicolo.attivo ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                        color: veicolo.attivo ? '#4caf50' : '#9e9e9e',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: veicolo.attivo ? '#4caf50' : '#9e9e9e' },
                      }}
                    />
                  </Box>

                  {/* Info veicolo */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {veicolo.tipo && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {veicolo.tipo}
                        </Typography>
                      </Box>
                    )}
                    {veicolo.note && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <NotesIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.3 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          {veicolo.note}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Azioni */}
                  {isAdmin && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        pt: 2,
                        mt: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(veicolo)}
                        sx={{
                          bgcolor: 'rgba(229, 0, 0, 0.1)',
                          color: '#e50000',
                          '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.2)' },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(veicolo)}
                        sx={{
                          bgcolor: 'rgba(244, 67, 54, 0.1)',
                          color: '#f44336',
                          '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          /* Vista Desktop - Tabella */
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflowX: 'auto',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Veicolo</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Note</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Stato</TableCell>
                  {isAdmin && (
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Azioni</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedVeicoli.map((veicolo) => (
                  <TableRow
                    key={veicolo.id}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.02)' },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: veicolo.attivo ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                            color: veicolo.attivo ? '#4caf50' : '#9e9e9e',
                          }}
                        >
                          <CarIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              color: '#e50000',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {veicolo.targa}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {veicolo.modello}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {veicolo.tipo ? (
                        <Chip
                          label={veicolo.tipo}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {veicolo.note || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={veicolo.attivo ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : <CancelIcon sx={{ fontSize: '14px !important' }} />}
                        label={veicolo.attivo ? 'Attivo' : 'Inattivo'}
                        size="small"
                        sx={{
                          bgcolor: veicolo.attivo ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                          color: veicolo.attivo ? '#4caf50' : '#9e9e9e',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: veicolo.attivo ? '#4caf50' : '#9e9e9e' },
                        }}
                      />
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Modifica">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(veicolo)}
                              sx={{
                                color: '#e50000',
                                '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.1)' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(veicolo)}
                              sx={{
                                color: '#f44336',
                                '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Paginazione */}
        {filteredVeicoli.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <TablePagination
              component="div"
              count={filteredVeicoli.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Righe per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
              sx={{
                bgcolor: '#fafafa',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Paper>
        )}
      </Container>

      {/* FAB mobile per nuovo veicolo */}
      {isAdmin && isMobile && (
        <Fab
          color="primary"
          onClick={() => handleOpenDialog()}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            bgcolor: '#e50000',
            boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
            '&:hover': {
              bgcolor: '#c50000',
            },
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog Nuovo/Modifica Veicolo */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header Dialog */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            color: 'white',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#e50000' }}>
                {currentVeicolo ? <EditIcon /> : <AddIcon />}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {currentVeicolo ? 'Modifica Veicolo' : 'Nuovo Veicolo'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  {currentVeicolo ? 'Modifica i dati del veicolo' : 'Inserisci i dati del nuovo veicolo'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="targa"
                label="Targa"
                fullWidth
                required
                value={formData.targa}
                onChange={handleInputChange}
                placeholder="Es: AB123CD"
                inputProps={{ style: { textTransform: 'uppercase' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="modello"
                label="Modello"
                fullWidth
                required
                value={formData.modello}
                onChange={handleInputChange}
                placeholder="Es: Fiat Doblò"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CarIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="tipo"
                label="Tipo"
                fullWidth
                value={formData.tipo}
                onChange={handleInputChange}
                placeholder="Es: Furgone, Auto, Camion"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TruckIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="note"
                label="Note"
                fullWidth
                multiline
                rows={3}
                value={formData.note}
                onChange={handleInputChange}
                placeholder="Note aggiuntive sul veicolo"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: formData.attivo ? 'rgba(76, 175, 80, 0.08)' : 'rgba(158, 158, 158, 0.08)',
                  border: '1px solid',
                  borderColor: formData.attivo ? 'rgba(76, 175, 80, 0.3)' : 'divider',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      name="attivo"
                      checked={formData.attivo}
                      onChange={handleInputChange}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#4caf50',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#4caf50',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Veicolo {formData.attivo ? 'Attivo' : 'Inattivo'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.attivo
                          ? 'Il veicolo è disponibile per gli interventi'
                          : 'Il veicolo non sarà disponibile per nuovi interventi'}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#e50000',
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
              '&:hover': { bgcolor: '#c50000' },
            }}
          >
            {currentVeicolo ? 'Salva Modifiche' : 'Crea Veicolo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' },
        }}
      >
        <Box
          sx={{
            bgcolor: '#f44336',
            color: 'white',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              <DeleteIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Conferma Eliminazione
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Typography>
            Sei sicuro di voler eliminare il veicolo{' '}
            <strong>{veicoloToDelete?.targa}</strong> - {veicoloToDelete?.modello}?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            Se il veicolo è associato a degli interventi, non potrà essere eliminato.
            In tal caso, disattivalo invece di eliminarlo.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDeleteDialog} sx={{ borderRadius: 2 }}>
            Annulla
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              bgcolor: '#f44336',
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              '&:hover': { bgcolor: '#d32f2f' },
            }}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Veicoli;
